import { useCallback, useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";

import { useUser } from "../lib/rbac/useUser.ts";
import {
  clearManagerStatus,
  fetchManagerStatus,
  readManagerStatus,
  writeManagerStatus,
  type ManagerStatus,
} from "./manager-status.ts";

export function useManagerStatus() {
  const user = useUser();
  const userId = user?.id ?? user?.email;
  const [status, setStatus] = useState<ManagerStatus | null>(() =>
    userId == null ? null : readManagerStatus(userId),
  );
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestVersion = useRef(0);

  const refresh = useCallback(async (signal?: AbortSignal) => {
    const version = ++requestVersion.current;
    const token = Cookies.get("accessToken");
    if (userId == null || !token) {
      setStatus(null);
      setVerified(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const nextStatus = await fetchManagerStatus(token, signal);
      if (signal?.aborted || version !== requestVersion.current) return;
      writeManagerStatus(userId, nextStatus);
      setStatus(nextStatus);
      setVerified(true);
      setError(null);
    } catch (cause) {
      if (signal?.aborted || version !== requestVersion.current) return;
      clearManagerStatus(userId);
      setStatus(null);
      setVerified(false);
      setError(cause instanceof Error ? cause.message : "Unable to load manager status.");
    } finally {
      if (!signal?.aborted && version === requestVersion.current) setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal);
    return () => {
      ++requestVersion.current;
      controller.abort();
    };
  }, [refresh]);

  useEffect(() => {
    const handleFocus = () => { void refresh(); };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [refresh]);

  return {
    status: verified ? status : null,
    cachedStatus: status,
    isLeaveManager: verified && status?.isLeaveManager === true,
    isReportingManager: verified && status?.isReportingManager === true,
    verified,
    loading,
    error,
    refresh,
  };
}
